import React, { useEffect, useState } from "react";
import { adminApi, formatApiError, useAuth } from "../../../context/AuthContext";
import { Button } from "../../ui/button";
import { Label } from "../../ui/label";
import { Textarea } from "../../ui/textarea";
import { toast } from "sonner";
import { Loader2, Save, RotateCcw } from "lucide-react";

/**
 * Invoice-disclaimer settings — Phase 18.2 admin surface.
 * Owner/Admin/Manager can view; only Owner/Admin can edit/reset (backend enforces).
 */
export default function InvoiceDisclaimerSection() {
  const { user, hasPermission } = useAuth();
  const canEdit = ["owner", "admin"].includes(user?.role);
  const canView = canEdit || user?.role === "manager" || hasPermission("customers:view");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [text, setText] = useState("");
  const [defaultValue, setDefaultValue] = useState("");
  const [isDefault, setIsDefault] = useState(true);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [updatedBy, setUpdatedBy] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await adminApi.get("/admin/document-settings/invoice-disclaimer");
      setText(r.data?.value || "");
      setDefaultValue(r.data?.default_value || "");
      setIsDefault(!!r.data?.is_default);
      setUpdatedAt(r.data?.updated_at || null);
      setUpdatedBy(r.data?.updated_by || null);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  };

  useEffect(() => { if (canView) refresh(); /* eslint-disable-next-line */ }, []);

  if (!canView) {
    return (
      <div className="bg-white border border-gray-200 p-10 text-center" data-testid="disclaimer-no-access">
        <h2 className="font-chivo font-black text-xl text-[#0A192F]">Geen toegang tot factuurdisclaimer.</h2>
      </div>
    );
  }

  const save = async () => {
    setSaving(true);
    try {
      const r = await adminApi.put("/admin/document-settings/invoice-disclaimer", { text });
      toast.success("Factuurdisclaimer opgeslagen");
      setIsDefault(!!r.data?.is_default);
      setUpdatedAt(r.data?.updated_at || null);
      setUpdatedBy(r.data?.updated_by || null);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSaving(false); }
  };

  const reset = async () => {
    if (!window.confirm("Weet u zeker dat u de disclaimer terugzet naar de standaardtekst?")) return;
    setSaving(true);
    try {
      const r = await adminApi.post("/admin/document-settings/invoice-disclaimer/reset");
      setText(r.data?.value || "");
      setIsDefault(true);
      setUpdatedAt(null);
      setUpdatedBy(null);
      toast.success("Disclaimer teruggezet naar standaard");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="invoice-disclaimer-section">
      <div className="bg-white border border-gray-200 p-6">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h2 className="font-chivo font-bold text-lg text-[#0A192F]">Factuurdisclaimer</h2>
            <p className="text-sm text-gray-600 mt-1 max-w-3xl">
              De disclaimer-tekst die onderaan elke door klanten gegenereerde factuur / waardeverklaring
              komt te staan. Owner en Admin kunnen wijzigen, Manager kan alleen lezen.
            </p>
          </div>
          <div className="text-xs text-gray-500 text-right">
            <div>Status: <span className={isDefault ? "text-amber-700" : "text-green-700 font-bold"}>{isDefault ? "Standaard" : "Aangepast"}</span></div>
            {updatedAt && <div>Gewijzigd: {new Date(updatedAt).toLocaleString("nl-NL")}</div>}
            {updatedBy && <div>Door: {updatedBy}</div>}
          </div>
        </div>

        {loading ? (
          <div className="py-10 text-center text-gray-500"><Loader2 className="w-4 h-4 inline animate-spin mr-2" /> Laden...</div>
        ) : (
          <>
            <div className="mt-4">
              <Label className="text-xs uppercase tracking-wider">Disclaimer-tekst</Label>
              <Textarea
                rows={10}
                value={text}
                onChange={(e) => setText(e.target.value)}
                disabled={!canEdit || saving}
                className="rounded-none font-mono text-[12px] mt-2"
                data-testid="invoice-disclaimer-textarea"
              />
              <p className="text-xs text-gray-500 mt-2">
                Laat leeg om de disclaimer niet op de PDF te tonen.
                De tekst wordt &eacute;&eacute;n-op-&eacute;&eacute;n op elke nieuwe factuur opgenomen
                (huidige facturen blijven hun eigen snapshot behouden).
              </p>
            </div>

            {canEdit && (
              <div className="mt-4 flex flex-wrap gap-2">
                <Button
                  onClick={save}
                  disabled={saving}
                  className="rounded-none bg-[#082c50] hover:bg-[#1a5193] text-white"
                  data-testid="invoice-disclaimer-save"
                >
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Opslaan
                </Button>
                <Button
                  variant="outline"
                  className="rounded-none"
                  onClick={reset}
                  disabled={saving}
                  data-testid="invoice-disclaimer-reset"
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Terug naar standaard
                </Button>
              </div>
            )}

            {!canEdit && (
              <p className="mt-4 text-xs text-amber-700">
                U heeft alleen leesrechten — vraag Owner of Admin om de tekst te wijzigen.
              </p>
            )}

            <details className="mt-6 text-xs text-gray-600">
              <summary className="cursor-pointer hover:text-brand-900">Standaardtekst tonen</summary>
              <pre className="mt-2 whitespace-pre-wrap bg-gray-50 border border-gray-200 p-3 font-mono text-[11px]" data-testid="invoice-disclaimer-default">
                {defaultValue}
              </pre>
            </details>
          </>
        )}
      </div>
    </div>
  );
}
